import { useQuery } from '@apollo/client'
import { PortableText } from '@portabletext/react'
import { Accordion } from '@szhsin/react-accordion'
import cn from 'classnames'
import Head from 'next/head'
import React from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'

import AccordionItem from '../components/AccordionItem'
import RenderPageBuilder from '../components/RenderPageBuilder'
import { HELP_CENTER } from '../graphql/queries/helpCenter'
import sanity from '../sanity'
import { sanityPageQuery } from '../sanity/page'
import { HelpCenter } from '../types/helpCenter'
import { enter, exit } from '../utils/animation'
import { getBrandName, getDomain } from '../utils/substores.config'

type Props = {
  page: any
  faqs: any
}

const Help = (props: Props) => {
  const hero = props.page.pageBuilder.filter(el => el._type === 'hero' || el._type === 'smallHero')
  const rest = props.page.pageBuilder.filter(el => el._type !== 'hero' && el._type !== 'smallHero')

  const [activeFaqSection, setActiveFaqSection] = React.useState(0)

  // ---

  const { data, loading, error } = useQuery<HelpCenter>(HELP_CENTER)

  // ---

  const handleSetActiveFaqSection = (i: number) => (e: any) => {
    setActiveFaqSection(i)
  }

  return (
    <div>
      <Head>
        <title>Få hjælp her | {getBrandName('Justdrive')} - bil i 120 dage</title>
        <meta
          name="description"
          content={`Har du behov for hjælp til din bil i 120 dage så er det her du får hjælp af ${getBrandName('JUSTDRIVE')}. Vi hjælper dig med alt fra at booke bilen til en skade`}
        />
        <link rel="canonical" href={`${getDomain()}/fa-hjaelp-her/`} />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`Få hjælp her | ${getBrandName('Justdrive')} - bil i 120 dage`} />
        <meta
          property="og:description"
          content={`Har du behov for hjælp til din bil i 120 dage så er det her du får hjælp af ${getBrandName('JUSTDRIVE')}. Vi hjælper dig med alt fra at booke bilen til en skade`}
        />
        <meta property="og:url" content={`${getDomain()}/fa-hjaelp-her/`} />
        <meta property="og:site_name" content={getBrandName('Justdrive')} />
        <meta property="article:modified_time" content="2023-02-11T21:06:20+00:00" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:label1" content="Estimeret læsetid" />
        <meta name="twitter:data1" content="3 minutter" />
      </Head>
      <RenderPageBuilder blocks={hero} />

      {data && (
        <>
          <div id="faq" className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-8 mx-4 md:mx-8 mb-20 pt-20">
            <div
              className={cn('relative grid col-span-1 xl:col-span-10 xl:col-start-2 md:bg-neutral-200 rounded-full', {
                'md:grid-cols-1': data.helpCenter.length === 1,
                'md:grid-cols-2': data.helpCenter.length === 2,
                'md:grid-cols-3': data.helpCenter.length === 3,
                'md:grid-cols-4': data.helpCenter.length === 4,
              })}
            >
              <div
                className={cn('hidden md:block absolute h-full col-span-1 bg-neutral-500 rounded-full transition duration-500', {
                  'w-full': data.helpCenter.length === 1,
                  'w-1/2': data.helpCenter.length === 2,
                  'w-1/3': data.helpCenter.length === 3,
                  'w-1/4': data.helpCenter.length === 4,
                })}
                style={{ transform: `translateX(${activeFaqSection * 100}%)` }}
              />
              {data.helpCenter.map((el, i) => (
                <div
                  key={i}
                  onClick={handleSetActiveFaqSection(i)}
                  className={cn(
                    'relative cursor-pointer col-span-1 py-1 px-4 center text-center transition duration-500 ease-out mb-4 md:mb-0',
                    {
                      'font-bold md:font-normal md:text-white': activeFaqSection === i,
                    }
                  )}
                >
                  {el.name}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 mx-4 md:mx-8 mb-20">
            <div className="col-span-1 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-6 xl:col-start-4">
              <SwitchTransition>
                <Transition
                  key={activeFaqSection}
                  timeout={500}
                  in={true}
                  onEnter={enter}
                  onExit={exit}
                  mountOnEnter={true}
                  unmountOnExit={true}
                >
                  <Accordion className="faq-block" transition transitionTimeout={500} allowMultiple>
                    {data.helpCenter[activeFaqSection].articles.map(el => {
                      return (
                        <AccordionItem key={el.id} header={el.description}>
                          {/* <PortableText value={el.body as any} /> */}
                          <div dangerouslySetInnerHTML={{ __html: el.body }}></div>
                        </AccordionItem>
                      )
                    })}
                  </Accordion>
                </Transition>
              </SwitchTransition>
            </div>
          </div>
        </>
      )}

      <RenderPageBuilder blocks={rest} />
    </div>
  )
}

export async function getStaticProps() {
  const page = await sanity.fetch<any>(sanityPageQuery('help'))
  const faqs = await sanity.fetch(`*[_type == "faqs"] | order(_createdAt asc)`)

  return {
    props: {
      page,
      faqs,
    },
  }
}

export default Help
